import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "moment/locale/pt";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRestaurantOpenDays,
  fetchOpenClosedExceptions,
} from "../../actions/restaurantActions";
import TimeManagement from "./RegularShiftManagement";
import OpenClosedExceptions from "./OpenClosedExceptions";
import {
  Container,
  CardHeader,
  CardHeaderTitle,
  FormGroup,
  InsideFormGroup,
  Input,
  InlineText,
  ModernButton,
  ButtonGap,
  StyledLabel,
} from "../DefaultImports";
import styled, { createGlobalStyle } from "styled-components";
import { CustomModal } from "../CustomModal";
import CalendarCustomToolbar from "./CalendarCustomToolbar";

moment.locale("pt");
const localizer = momentLocalizer(moment);

const RestaurantCalendarOpenClosedDays = ({ restaurant_id }) => {
  const dispatch = useDispatch();
  const { openDays, isLoadingOpenDays } = useSelector(
    (state) => state.restaurantOpenDaysOfTheWeek
  );
  const openClosedExceptions = useSelector(
    (state) => state.openClosedExceptions
  );
  const { exceptions } = openClosedExceptions;

  const [events, setEvents] = useState([]);
  const [selectedOption, setSelectedOption] = useState("regular_shift");
  const [mode, setMode] = useState("create");
  const [selected_obj_id, setSelected_obj_id] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment());

  const [calendarView, setCalendarView] = useState("month");
  const calendarContainerRef = React.useRef(null);

  const [calendarHeight, setCalendarHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (calendarContainerRef.current) {
        const top = calendarContainerRef.current.getBoundingClientRect().top;
        setCalendarHeight(window.innerHeight - top);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const modalTitle =
    selectedOption === "closed_exception"
      ? "Criar fecho pontual"
      : mode === "edit"
      ? "Editar turno"
      : selectedOption === "open_exception"
      ? "Criar turno pontual"
      : "Criar turno regular";

  useEffect(() => {
    dispatch(fetchRestaurantOpenDays());
    dispatch(fetchOpenClosedExceptions(restaurant_id));
  }, [dispatch, restaurant_id]);

  useEffect(() => {
    if (!Array.isArray(openDays)) return;

    const weekdaysMap = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 0,
    };

    const firstDayOfMonth = currentDate.clone().startOf("month");
    const lastDayOfMonth = currentDate.clone().endOf("month");

    const formattedOpenDaysEvents = openDays.flatMap((day) =>
      Object.keys(weekdaysMap).reduce((acc, weekday) => {
        if (day[weekday]) {
          let startDate = firstDayOfMonth.clone().day(weekdaysMap[weekday]);
          if (startDate.isBefore(firstDayOfMonth)) {
            startDate.add(7, "days");
          }

          const zonesFormatted = (day.list_of_zones || "")
            .split(",")
            .filter(Boolean)
            .join("\n");

          const formattedStart = moment(day.start_time, "HH:mm:ss").format(
            "HH:mm"
          );
          const formattedEnd = moment(day.end_time, "HH:mm:ss").format("HH:mm");

          while (startDate.isSameOrBefore(lastDayOfMonth)) {
            acc.push({
              id: day.id,
              title: `${formattedStart} - ${formattedEnd}${
                zonesFormatted ? `\n${zonesFormatted}` : ""
              }`,
              start: moment(
                `${startDate.format("YYYY-MM-DD")} ${day.start_time}`,
                "YYYY-MM-DD HH:mm:ss"
              ).toDate(),
              end: moment(
                `${startDate.format("YYYY-MM-DD")} ${day.end_time}`,
                "YYYY-MM-DD HH:mm:ss"
              ).toDate(),
              type: "regular_shift",
              allDay: false,
            });
            startDate.add(7, "days");
          }
        }
        return acc;
      }, [])
    );

    const formattedExceptionsEvents = Array.isArray(exceptions)
      ? exceptions.flatMap((exception) => {
          const events = [];
          const startDate = moment(exception.closed_days_start, "YYYY-MM-DD");
          const endDate = exception.closed_days_end
            ? moment(exception.closed_days_end, "YYYY-MM-DD")
            : startDate;

          const eventStartTime = exception.start_time || "00:00:00";
          const eventEndTime = exception.end_time || "23:59:00";

          const startFormatted = moment(eventStartTime, "HH:mm:ss").format(
            "HH:mm"
          );
          const endFormatted = moment(eventEndTime, "HH:mm:ss").format("HH:mm");

          const timeRange =
            startFormatted === "00:00" && endFormatted === "23:59"
              ? ""
              : `${startFormatted} - ${endFormatted}`;

          const zonesFormatted = (exception.list_of_zones || "")
            .split(",")
            .filter(Boolean)
            .join("\n");

          const titlePrefix =
            exception.type_of_exception === "closed_exception"
              ? "Fechado"
              : exception.type_of_exception === "open_exception"
              ? "Aberto (Exceção)"
              : "Limitado";

          let current = startDate.clone();
          while (current.isSameOrBefore(endDate)) {
            events.push({
              id: exception.id,
              title: `${timeRange ? `${timeRange}\n` : ""}${
                zonesFormatted || titlePrefix
              }`,
              start: moment(
                `${current.format("YYYY-MM-DD")} ${eventStartTime}`,
                "YYYY-MM-DD HH:mm:ss"
              ).toDate(),
              end: moment(
                `${current.format("YYYY-MM-DD")} ${eventEndTime}`,
                "YYYY-MM-DD HH:mm:ss"
              ).toDate(),
              type: exception.type_of_exception,
              allDay: startFormatted === "00:00" && endFormatted === "23:59",
            });

            current.add(1, "day");
          }

          return events;
        })
      : [];

    setEvents([...formattedOpenDaysEvents, ...formattedExceptionsEvents]);
  }, [openDays, exceptions, currentDate]);

  const handleSelectSlot = useCallback(({ start, end }) => {
    setMode("create");
    setSelectedOption("regular_shift");
    setSelected_obj_id(null);
    setModalIsOpen(true);
    console.log("ggs ::");
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setMode("edit");
    setSelected_obj_id(event.id);
    setSelectedOption(event.type);
    setModalIsOpen(true);
  }, []);

  const calendarRef = React.useRef(null);

  const handleScroll = useCallback((e) => {
    if (calendarRef.current) {
      const headerContent = calendarRef.current.querySelector(
        ".rbc-time-header-content"
      );
      const timeContent =
        calendarRef.current.querySelector(".rbc-time-content");

      if (headerContent && timeContent) {
        headerContent.style.transform = `translateX(-${timeContent.scrollLeft}px)`;
      }
    }
  }, []);

  if (isLoadingOpenDays) return <div>Loading...</div>;

  return (
    <>
      <CalendarCustomScroll />
      <div className="flex justify-between items-center mb-2">
        <ResponsiveButtonGroup
          style={{ borderTop: "1px #99B0AD solid", paddingTop: "10px" }}
        >
          <ModernButton
            color={"#6BA368"}
            onClick={() => {
              setSelectedOption("regular_shift");
              setMode("create");
              setSelected_obj_id(null);
              setModalIsOpen(true);
            }}
          >
            Turno regular
          </ModernButton>

          <ModernButton
            color={"#D9A53A"}
            onClick={() => {
              setSelectedOption("open_exception");
              setMode("create");
              setSelected_obj_id(null);
              setModalIsOpen(true);
            }}
          >
            Turno pontual
          </ModernButton>

          <ModernButton
            color={"#822839"}
            onClick={() => {
              setModalIsOpen(true);
              setMode("create");
              setSelectedOption("closed_exception");
            }}
          >
            Fecho pontual
          </ModernButton>
        </ResponsiveButtonGroup>
      </div>

      <div
        ref={calendarContainerRef}
        style={{ height: `${calendarHeight}px`, overflow: "hidden" }}
      >
        <Calendar
          style={{
            border: "none",
          }}
          onView={(view) => {
            setCalendarView(view);

            if (view === "week") {
              setTimeout(() => {
                if (!calendarContainerRef.current) return;

                const timeContent =
                  calendarContainerRef.current.querySelector(
                    ".rbc-time-content"
                  );
                const events = calendarContainerRef.current.querySelectorAll(
                  ".rbc-time-content .rbc-event"
                );

                if (timeContent && events.length > 0) {
                  // Get the earliest event visually
                  let earliestTop = Infinity;
                  events.forEach((event) => {
                    if (event.offsetTop < earliestTop) {
                      earliestTop = event.offsetTop;
                    }
                  });

                  // Scroll to just above the earliest event
                  timeContent.scrollTop = Math.max(earliestTop - 20, 0);
                }
              }, 100); // Give time for layout/rendering
            }
          }}
          ref={calendarRef}
          onScroll={handleScroll}
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onNavigate={(date) => setCurrentDate(moment(date))}
          culture="pt"
          min={new Date(new Date().setHours(0, 0, 0, 0))}
          max={new Date(new Date().setHours(23, 59, 59, 999))}
          defaultView="month"
          views={["month", "week"]}
          messages={{
            today: "Hoje",
            previous: "Anterior",
            next: "Próximo",
            month: "Mês",
            week: "Semana",
            day: "Dia",
          }}
          components={{
            toolbar: CalendarCustomToolbar,
          }}
          eventPropGetter={(event) => {
            let style = {
              backgroundColor: "#6BA368",
              borderRadius: "5px",
              color: "white",
              border: "none",
              display: "block",
            };

            if (event.type === "closed_exception") {
              style.backgroundColor = "#822839";
            } else if (event.type === "open_exception") {
              style.backgroundColor = "#D9A53A";
            } else if (event.type === "limited") {
              style.backgroundColor = "#ffc107";
              style.color = "#333";
            }

            return { style };
          }}
        />
      </div>

      <CustomModal
        show={modalIsOpen}
        onHide={() => setModalIsOpen(false)}
        title={modalTitle}
        centered={false}
      >
        <div
          style={{ overflow: "visible", position: "relative", zIndex: 1200 }}
        >
          {selectedOption === "regular_shift" && (
            <TimeManagement
              shiftId={selected_obj_id}
              mode={mode}
              restaurantId={restaurant_id}
              showModal={modalIsOpen}
              setShowModal={setModalIsOpen}
            />
          )}

          {(selectedOption === "open_exception" ||
            selectedOption === "closed_exception" ||
            selectedOption === "limited") && (
            <OpenClosedExceptions
              exception_id={selected_obj_id}
              type_of_exception={selectedOption}
              mode={mode}
              restaurant_id={restaurant_id}
              setShowModal={setModalIsOpen}
            />
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default RestaurantCalendarOpenClosedDays;

export const CalendarCustomScroll = createGlobalStyle`
  /* Main calendar container */
  .rbc-calendar {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;
    overflow: visible;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  @media (min-width: 768px) {
    .rbc-calendar { padding-bottom: 40px; }
  }

  .rbc-event .rbc-event-label { display: none !important; }

  /* Toolbar */
  .rbc-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden;
    gap: 0;
    border: none;
  }
  .rbc-toolbar .rbc-btn-group { display: flex; gap: 0; margin: 0 !important; padding: 0 !important; flex: 0 0 auto; }
  .rbc-toolbar-label { margin: 0 !important; padding: 0 !important; font-size: 14px; font-weight: 500; white-space: nowrap; }
  .rbc-toolbar button { font-size: 14px; padding: 6px 10px; margin: 0 !important; }
  .rbc-toolbar::-webkit-scrollbar { display: none; }
  .rbc-toolbar { -ms-overflow-style: none; scrollbar-width: none; }

  /* Week/Day view main container */
  .rbc-time-view {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;          /* mobile default */
    border-radius: 4px;
  }

  /* Time header container (left column + days) */
  .rbc-time-header {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #99B0AD;
    padding-left: 60px;        /* mantém padding desktop */
  }
  @media (max-width: 600px) {
    .rbc-time-header { padding-left: 40px; }  /* mantém padding mobile */
  }

  .rbc-time-header-content {
    display: flex;
    min-width: fit-content;
  }

  /* Dias da semana */
  .rbc-header {
    flex: 1 0 120px;
    padding: 8px;
    text-align: center;
    border-right: 1px solid #e3e6e8;
    background: #99B0AD;
    font-size: 0.9rem;
    font-weight: 400;
    color: white;
  }

  .rbc-time-content {
    display: flex;
    flex: 1;
    overflow: auto;           /* mobile scroll */
    position: relative;
  }

  .rbc-time-gutter {
    position: sticky;
    left: 0;
    background: white;
    z-index: 10;
    width: 60px;
    border-right: 1px solid #ddd;
    color: #4D5E5B;
    font-size: 12px;
    text-align: center !important;
  }
  @media (max-width: 600px) {
    .rbc-time-gutter { width: 40px; font-size: 10px; }
  }

  .rbc-day-slot { flex: 1 0 120px; }

  .rbc-time-header::-webkit-scrollbar { display: none; }
  .rbc-time-content::-webkit-scrollbar { height: 8px; }
  .rbc-time-content::-webkit-scrollbar-track { background: #f1f1f1; }
  .rbc-time-content::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }

  .rbc-allday-cell { display: none; }

  .rbc-event {
    font-size: 12px;
    padding: 3px 6px;
    border-radius: 5px;
    color: white;
    white-space: pre-line !important;
    line-height: 1.3;
    margin: 2px 2px;
    transition: background-color 0.2s ease;
  }

  /* ---------- DESKTOP (scroll no topo) ---------- */
  @media (min-width: 601px) {
    .rbc-time-view  { overflow: auto; }      /* scroll cobre header + grelha */
    .rbc-time-content { overflow: visible; } /* grelha sem scroll próprio */

    /* compensa largura da scrollbar (17 px) no topo */
    .rbc-time-header-content::after {
      content: '';
      display: block;
      width: 17px;
    }
  }

  /* ----- Vista mensal (inalterada) ----- */
  .rbc-month-header .rbc-header {
    background: #99B0AD;
    color: white;
    font-size: 13px;
    font-weight: 500;
    padding: 10px 4px;
    text-align: center;
    border-right: 1px solid #e3e6e8;
    flex: 1;
    min-width: 0;
  }
  .rbc-month-view {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: auto;
    border-radius: 4px;
  }
  .rbc-month-view .rbc-month-header {
    position: sticky;
    top: 0;
    z-index: 5;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }
  .rbc-month-header { display: flex; }
  .rbc-month-row { display: flex; min-height: 120px; }
  .rbc-row-bg { display: flex; flex: 1; }
  .rbc-day-bg { flex: 1; border-right: 1px solid #ddd; border-bottom: 1px solid #ddd; }
  .rbc-date-cell { padding: 4px; text-align: right; }
  .rbc-month-row .rbc-row-content { position: relative; z-index: 4; }
  .rbc-month-row .rbc-events-container {
    margin: 0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
  .rbc-month-view .rbc-event {
    position: relative;
    width: calc(100% - 4px);
    height: auto;
    line-height: 1.4;
    margin: 2px;
    padding: 2px 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ResponsiveButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;
  background: transparent !important;

  button {
    padding: 5px 12px;
    font-size: 14px;
    white-space: nowrap;
    max-width: fit-content;
    width: auto;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    padding: 0 5px;
    button {
      width: auto;
      max-width: 180px;
      flex: 0 0 auto;
    }
  }
`;
